import {
  OPS_BY_TYPE,
  SCREEN_FIELD_TYPES,
  type LeafOp,
  type ScreenCompound,
  type ScreenField,
  type ScreenLeaf,
  type ScreenPredicate,
} from "../../api/types/screenCriteria";

/**
 * UI-side tree model + (de)serialisation for the multi-leaf predicate
 * builder (V2-2-U3 polish, session 268).
 *
 * The wire AST in [screenCriteria.ts](../api/types/screenCriteria.ts) is
 * the source of truth; this module mirrors it as a mutable
 * `BuilderNode` tree the React component can edit by index path, then
 * serialises back. Equality across the round-trip is structural: any
 * `(de)serialise(serialise(tree))` is equivalent to the original modulo
 * the local React `id` field.
 *
 * Backend invariants honoured here (mirror of
 * `workspaces/domain/saved_screens/value_objects._validate_*`):
 * * `not` groups carry exactly one term.
 * * `and` / `or` groups accept zero or more (an empty top-level `and`
 *   is the "match all" sentinel).
 * * Predicate tree depth must not exceed `_PREDICATE_MAX_DEPTH = 16`.
 *
 * Per Q161 (session 268 scoping): legacy bare-leaf criteria deserialise
 * by wrapping the leaf in an implicit top-level AND group so the
 * builder always edits a group root.
 */

let _idCounter = 0;
function newNodeId(): string {
  _idCounter += 1;
  return `pn-${_idCounter}`;
}

export type GroupConnector = "and" | "or" | "not";

export type LeafBuilderNode = {
  readonly kind: "leaf";
  readonly id: string;
  readonly field: ScreenField | "";
  readonly op: LeafOp | "";
  readonly value: string;
};

export type GroupBuilderNode = {
  readonly kind: "group";
  readonly id: string;
  readonly connector: GroupConnector;
  readonly children: ReadonlyArray<BuilderNode>;
};

export type BuilderNode = LeafBuilderNode | GroupBuilderNode;

/** Backend hard cap (mirrors `_PREDICATE_MAX_DEPTH` in
 * `workspaces/domain/saved_screens/value_objects.py`). */
export const BACKEND_MAX_DEPTH = 16;

/** Q160: depth > 3 surfaces a soft "deeply nested" warning chip. */
export const DEEPLY_NESTED_WARNING_DEPTH = 3;

export function newLeaf(): LeafBuilderNode {
  return { kind: "leaf", id: newNodeId(), field: "", op: "", value: "" };
}

export function newGroup(connector: GroupConnector = "and"): GroupBuilderNode {
  return {
    kind: "group",
    id: newNodeId(),
    connector,
    children: [newLeaf()],
  };
}

export function newEmptyRoot(): GroupBuilderNode {
  return {
    kind: "group",
    id: newNodeId(),
    connector: "and",
    children: [newLeaf()],
  };
}

export function isLeafNode(node: BuilderNode): node is LeafBuilderNode {
  return node.kind === "leaf";
}

export function isGroupNode(node: BuilderNode): node is GroupBuilderNode {
  return node.kind === "group";
}

function isCompoundOp(op: ScreenPredicate["op"]): op is GroupConnector {
  return op === "and" || op === "or" || op === "not";
}

function isCompoundPredicate(p: ScreenPredicate): p is ScreenCompound {
  return isCompoundOp(p.op);
}

function isLeafPredicate(p: ScreenPredicate): p is ScreenLeaf {
  return !isCompoundOp(p.op);
}

/**
 * Walk the tree to the node at `path` (root-relative index path) and
 * return a new root with the node replaced by `updater(target)`.
 * Returns the original root unchanged if the path is invalid.
 */
export function updateAtPath(
  root: GroupBuilderNode,
  path: ReadonlyArray<number>,
  updater: (node: BuilderNode) => BuilderNode,
): GroupBuilderNode {
  if (path.length === 0) {
    const next = updater(root);
    return isGroupNode(next) ? next : root;
  }
  const head = path[0];
  if (head === undefined || head < 0 || head >= root.children.length) {
    return root;
  }
  const child = root.children[head];
  if (child === undefined) {
    return root;
  }
  const rest = path.slice(1);
  let nextChild: BuilderNode;
  if (rest.length === 0) {
    nextChild = updater(child);
  } else if (isGroupNode(child)) {
    nextChild = updateAtPath(child, rest, updater);
  } else {
    return root;
  }
  const nextChildren = [...root.children];
  nextChildren[head] = nextChild;
  return { ...root, children: nextChildren };
}

/** Insert `node` at the end of the group reached by `groupPath`. */
export function appendChild(
  root: GroupBuilderNode,
  groupPath: ReadonlyArray<number>,
  node: BuilderNode,
): GroupBuilderNode {
  return updateAtPath(root, groupPath, (target) => {
    if (!isGroupNode(target)) {
      return target;
    }
    return { ...target, children: [...target.children, node] };
  });
}

/** Remove the child at the given path from its parent. */
export function removeAtPath(
  root: GroupBuilderNode,
  path: ReadonlyArray<number>,
): GroupBuilderNode {
  if (path.length === 0) {
    return root;
  }
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  return updateAtPath(root, parentPath, (target) => {
    if (!isGroupNode(target)) {
      return target;
    }
    const nextChildren = target.children.filter((_, i) => i !== index);
    return { ...target, children: nextChildren };
  });
}

/** Move a child up or down inside its parent by one slot. */
export function moveAtPath(
  root: GroupBuilderNode,
  path: ReadonlyArray<number>,
  direction: "up" | "down",
): GroupBuilderNode {
  if (path.length === 0) {
    return root;
  }
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  if (index === undefined) {
    return root;
  }
  return updateAtPath(root, parentPath, (target) => {
    if (!isGroupNode(target)) {
      return target;
    }
    const swap = direction === "up" ? index - 1 : index + 1;
    if (swap < 0 || swap >= target.children.length) {
      return target;
    }
    const nextChildren = [...target.children];
    const a = nextChildren[index];
    const b = nextChildren[swap];
    if (a === undefined || b === undefined) {
      return target;
    }
    nextChildren[index] = b;
    nextChildren[swap] = a;
    return { ...target, children: nextChildren };
  });
}

/** Maximum *group* depth in the tree (root group = depth 0). Leaves do
 * not add a level -- only nested groups do. This mirrors the depth the
 * UI tracks for `canAddGroupAtDepth` (the relevant cap is on group
 * nesting, since each compound node is what bumps the backend's
 * recursion depth at the AST level beyond what its leaves contribute).
 */
export function maxDepth(root: GroupBuilderNode): number {
  function walk(group: GroupBuilderNode, depth: number): number {
    let max = depth;
    for (const child of group.children) {
      if (isGroupNode(child)) {
        max = Math.max(max, walk(child, depth + 1));
      }
    }
    return max;
  }
  return walk(root, 0);
}

/** True when adding a *group* child at `groupDepth` would exceed the
 * backend depth cap. Leaves are always allowed because they don't add a
 * level. */
export function canAddGroupAtDepth(groupDepth: number): boolean {
  return groupDepth + 1 <= BACKEND_MAX_DEPTH;
}

export type SerialiseSuccess = {
  ok: true;
  criteria: ScreenPredicate;
};

export type SerialiseFailure = {
  ok: false;
  error: string;
};

export type SerialiseResult = SerialiseSuccess | SerialiseFailure;

/**
 * Serialise the tree to the wire AST. Empty leaves and groups whose
 * children all collapse to empty are pruned. Returns a failure if the
 * tree violates a backend invariant the UI can't auto-resolve (incomplete
 * non-empty leaf, NOT group with !=1 children after pruning).
 */
export function serialiseTree(root: GroupBuilderNode): SerialiseResult {
  const result = serialiseNode(root);
  if (!result.ok) {
    return result;
  }
  if (result.value === null) {
    return { ok: true, criteria: { op: "and", terms: [] } };
  }
  if (isLeafPredicate(result.value)) {
    return {
      ok: true,
      criteria: { op: "and", terms: [result.value] },
    };
  }
  return { ok: true, criteria: result.value };
}

type NodeSerialiseResult =
  | { ok: true; value: ScreenPredicate | null }
  | { ok: false; error: string };

function serialiseNode(node: BuilderNode): NodeSerialiseResult {
  if (isGroupNode(node)) {
    const terms: ScreenPredicate[] = [];
    for (const child of node.children) {
      const sub = serialiseNode(child);
      if (!sub.ok) {
        return sub;
      }
      if (sub.value !== null) {
        terms.push(sub.value);
      }
    }
    if (node.connector === "not") {
      if (terms.length !== 1) {
        return {
          ok: false,
          error:
            "NOT groups require exactly one filled condition. " +
            "Remove the extras or change the connector.",
        };
      }
      const compound: ScreenCompound = { op: "not", terms };
      return { ok: true, value: compound };
    }
    if (terms.length === 0) {
      return { ok: true, value: null };
    }
    const compound: ScreenCompound = { op: node.connector, terms };
    return { ok: true, value: compound };
  }
  return serialiseLeaf(node);
}

function serialiseLeaf(node: LeafBuilderNode): NodeSerialiseResult {
  if (node.field === "" && node.op === "" && node.value === "") {
    return { ok: true, value: null };
  }
  if (node.field === "" || node.op === "") {
    return {
      ok: false,
      error: "Every condition needs a field and an operator.",
    };
  }
  const fieldType = SCREEN_FIELD_TYPES[node.field];
  if (!(OPS_BY_TYPE[fieldType] as ReadonlyArray<LeafOp>).includes(node.op)) {
    return {
      ok: false,
      error: `Operator ${node.op} is not valid for field ${node.field}.`,
    };
  }
  if (node.op === "is_null" || node.op === "is_not_null") {
    const leaf: ScreenLeaf = { op: node.op, field: node.field };
    return { ok: true, value: leaf };
  }
  if (node.op === "in") {
    const tokens = node.value
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (tokens.length === 0) {
      return {
        ok: false,
        error: `Operator 'in' on ${node.field} needs at least one value.`,
      };
    }
    const coerced =
      fieldType === "numeric"
        ? tokens.map((t) => (Number.isNaN(Number(t)) ? t : Number(t)))
        : tokens;
    const leaf: ScreenLeaf = { op: "in", field: node.field, value: coerced };
    return { ok: true, value: leaf };
  }
  const coerced: string | number =
    fieldType === "numeric" && node.value !== "" && !Number.isNaN(Number(node.value))
      ? Number(node.value)
      : node.value;
  const leaf: ScreenLeaf = {
    op: node.op,
    field: node.field,
    value: coerced,
  };
  return { ok: true, value: leaf };
}

/**
 * Deserialise a wire predicate to a builder tree. Per Q161, a bare leaf
 * at the top level wraps in an implicit top-level AND group with the
 * leaf as its sole child.
 */
export function deserialiseTree(predicate: ScreenPredicate): GroupBuilderNode {
  if (isCompoundPredicate(predicate)) {
    return {
      kind: "group",
      id: newNodeId(),
      connector: predicate.op,
      children: predicate.terms.map(deserialiseNode),
    };
  }
  return {
    kind: "group",
    id: newNodeId(),
    connector: "and",
    children: [deserialiseLeaf(predicate)],
  };
}

function deserialiseNode(predicate: ScreenPredicate): BuilderNode {
  if (isCompoundPredicate(predicate)) {
    return {
      kind: "group",
      id: newNodeId(),
      connector: predicate.op,
      children: predicate.terms.map(deserialiseNode),
    };
  }
  return deserialiseLeaf(predicate);
}

function deserialiseLeaf(predicate: ScreenLeaf): LeafBuilderNode {
  const id = newNodeId();
  switch (predicate.op) {
    case "is_null":
    case "is_not_null":
      return {
        kind: "leaf",
        id,
        field: predicate.field,
        op: predicate.op,
        value: "",
      };
    case "in":
      return {
        kind: "leaf",
        id,
        field: predicate.field,
        op: "in",
        value: predicate.value.map((v) => String(v)).join(", "),
      };
    default:
      return {
        kind: "leaf",
        id,
        field: predicate.field,
        op: predicate.op,
        value: String(predicate.value),
      };
  }
}
